import {
  EnemyType,
  HazardType,
  LevelTheme,
  ObstacleType,
  TargetConfiguration,
  TargetState,
} from '../types.js';
import type {
  EnemyBehaviorId,
  StageDefinition,
  StageHazardEvent,
  StageId,
  StageObstacleEvent,
  StageSpawnEvent,
  TargetDefinition,
} from './types.js';
import { validateStageCatalog } from './validate.js';

const targetConfigurations = [
  {
    id: TargetConfiguration.Armored,
    healthThreshold: 1,
    speedScale: 1,
    fireIntervalScale: 1.15,
    label: 'ARMORED',
  },
  {
    id: TargetConfiguration.Defensive,
    healthThreshold: 0.7,
    speedScale: 1.06,
    fireIntervalScale: 1,
    label: 'DEFENSIVE',
  },
  {
    id: TargetConfiguration.Exposed,
    healthThreshold: 0.4,
    speedScale: 0.94,
    fireIntervalScale: 0.82,
    label: 'EXPOSED',
  },
  {
    id: TargetConfiguration.Critical,
    healthThreshold: 0.15,
    speedScale: 0.8,
    fireIntervalScale: 0.68,
    label: 'CRITICAL',
  },
] as const;

/** The first half of every operation is a chase through regular opposition. */
const TARGET_CONTACT_T = 0.46;

function target(id: string, title: string, maxHealth: number, bounty: number): TargetDefinition {
  return {
    id,
    title,
    enemyType: EnemyType.ArmoredTarget,
    contactT: TARGET_CONTACT_T,
    escapeT: 0.985,
    maxHealth,
    bounty,
    configurations: targetConfigurations,
    components: [
      {
        id: `${id}_left_tire`,
        label: 'LEFT TIRE',
        maxHealth: 55,
        radius: 0.65,
        lateralOffset: -1.05,
        heightOffset: 0.55,
        forwardOffset: 0.7,
        exposedIn: [TargetConfiguration.Defensive, TargetConfiguration.Exposed, TargetConfiguration.Critical],
      },
      {
        id: `${id}_right_tire`,
        label: 'RIGHT TIRE',
        maxHealth: 55,
        radius: 0.65,
        lateralOffset: 1.05,
        heightOffset: 0.55,
        forwardOffset: 0.7,
        exposedIn: [TargetConfiguration.Defensive, TargetConfiguration.Exposed, TargetConfiguration.Critical],
      },
      {
        id: `${id}_engine`,
        label: 'ENGINE',
        maxHealth: 95,
        radius: 0.8,
        lateralOffset: 0,
        heightOffset: 1,
        forwardOffset: -1.25,
        exposedIn: [TargetConfiguration.Exposed, TargetConfiguration.Critical],
      },
    ],
  };
}

function spawn(
  id: string,
  t: number,
  enemyType: EnemyType,
  count: number,
  lateralSpread: number,
  behavior: EnemyBehaviorId,
): StageSpawnEvent {
  return { id, t, enemyType, count, lateralSpread, behavior };
}

function obstacle(
  id: string,
  t: number,
  obstacleType: ObstacleType,
  laterals: readonly number[],
): StageObstacleEvent {
  return { id, t, obstacleType, laterals };
}

function hazard(
  id: string,
  t: number,
  hazardType: HazardType,
  lateralOffset: number,
  label: string,
  damage = 14,
  radius = 1.7,
): StageHazardEvent {
  return {
    id,
    t,
    hazardType,
    lateralOffset,
    warningSeconds: 1.35,
    committedSeconds: 0.45,
    activeSeconds: 0.35,
    recoverySeconds: 0.8,
    radius,
    damage,
    label,
  };
}

const countryside: StageDefinition = {
  id: 'countryside',
  order: 1,
  theme: LevelTheme.Countryside,
  seed: 'lucky-countryside-01',
  title: 'Rolling Contact',
  location: 'North County Highway',
  briefing: 'Identify the courier, survive the escort screen, and disable the interceptor before the county line.',
  timeLimitSeconds: 190,
  target: target('rook', 'THE ROOK', 500, 5000),
  beats: [
    { id: 'country_intercept', startsAt: 0, endsAt: 0.24, title: 'Highway Intercept', objective: 'Cut through the courier advance screen', objectiveType: 'pursue', targetState: TargetState.Unknown },
    { id: 'country_trace', startsAt: 0.24, endsAt: TARGET_CONTACT_T, title: 'Signal Trace', objective: 'Destroy the escort and locate THE ROOK', objectiveType: 'make_contact', targetState: TargetState.Unknown },
    { id: 'country_contact', startsAt: TARGET_CONTACT_T, endsAt: 0.72, title: 'Rolling Contact', objective: 'Hold THE ROOK in range and break its armour', objectiveType: 'pursue', targetState: TargetState.Pursuit },
    { id: 'country_disable', startsAt: 0.72, endsAt: 1, title: 'County Line', objective: 'Expose components and disable THE ROOK', objectiveType: 'disable_target', targetState: TargetState.Exposed },
  ],
  spawns: [
    spawn('country_screen_1', 0.02, EnemyType.Sedan, 2, 2.4, 'escort'),
    spawn('country_bikes_1', 0.1, EnemyType.Motorcycle, 2, 2.8, 'flanker'),
    spawn('country_screen_2', 0.19, EnemyType.Sedan, 2, 3.1, 'escort'),
    spawn('country_drone_1', 0.27, EnemyType.Drone, 2, 3.4, 'aerial_harasser'),
    spawn('country_truck_1', 0.36, EnemyType.Truck, 1, 0, 'blocker'),
    spawn('country_bikes_2', 0.44, EnemyType.Motorcycle, 3, 3.1, 'flanker'),
    { ...spawn('country_target', TARGET_CONTACT_T, EnemyType.ArmoredTarget, 1, 0, 'target'), isTarget: true },
    spawn('country_screen_3', 0.55, EnemyType.Sedan, 3, 3.2, 'escort'),
    spawn('country_drone_2', 0.63, EnemyType.Drone, 2, 2.8, 'aerial_harasser'),
    spawn('country_truck_2', 0.7, EnemyType.Truck, 1, 0, 'blocker'),
    spawn('country_final_bikes', 0.8, EnemyType.Motorcycle, 3, 3.4, 'flanker'),
    spawn('country_final_screen', 0.9, EnemyType.Sedan, 2, 3, 'escort'),
  ],
  obstacles: [
    obstacle('country_barrels_1', 0.075, ObstacleType.Barrel, [-2.8, 2.5]),
    obstacle('country_barrier_1', 0.16, ObstacleType.Barrier, [-3.4]),
    obstacle('country_rocks_1', 0.25, ObstacleType.Rock, [1.8, 4.1]),
    obstacle('country_repair_1', 0.29, ObstacleType.RepairKit, [-7.4]),
    obstacle('country_wreck_1', 0.34, ObstacleType.Wreck, [-3.8, 0.2]),
    obstacle('country_barrels_2', 0.43, ObstacleType.Barrel, [-4, -1, 3]),
    obstacle('country_barrier_2', 0.58, ObstacleType.Barrier, [2.9]),
    obstacle('country_repair_2', 0.67, ObstacleType.RepairKit, [7.4]),
    obstacle('country_rocks_2', 0.69, ObstacleType.Rock, [-2.4, 2.6]),
    obstacle('country_wreck_2', 0.84, ObstacleType.Wreck, [3.4]),
    obstacle('country_barrels_3', 0.93, ObstacleType.Barrel, [-3.2, 0.4, 3.8]),
  ],
  hazards: [
    hazard('country_cargo_1', 0.34, HazardType.CargoDrop, -2.6, 'FALLING CARGO'),
    hazard('country_cargo_2', 0.59, HazardType.CargoDrop, 2.8, 'FALLING CARGO'),
    hazard('country_lane_1', 0.76, HazardType.LaneStrike, 0.4, 'ESCORT STRIKE', 16, 1.9),
  ],
};

const city: StageDefinition = {
  id: 'city',
  order: 2,
  theme: LevelTheme.City,
  seed: 'lucky-city-02',
  title: 'Gridlock',
  location: 'Central Business District',
  briefing: 'Thread civilian traffic, counter the target\'s intersection traps, and force it into the extraction corridor.',
  timeLimitSeconds: 195,
  target: target('magpie', 'MAGPIE', 560, 6500),
  beats: [
    { id: 'city_entry', startsAt: 0, endsAt: 0.24, title: 'Rush Hour', objective: 'Fight through traffic and clear the intercept route', objectiveType: 'pursue', targetState: TargetState.Unknown },
    { id: 'city_crossings', startsAt: 0.24, endsAt: TARGET_CONTACT_T, title: 'Cross Traffic', objective: 'Survive the intersections and trace MAGPIE', objectiveType: 'survive_hazard', targetState: TargetState.Unknown },
    { id: 'city_contact', startsAt: TARGET_CONTACT_T, endsAt: 0.7, title: 'Target Acquired', objective: 'Pin MAGPIE down beyond the blockade', objectiveType: 'pursue', targetState: TargetState.Pursuit },
    { id: 'city_extraction', startsAt: 0.7, endsAt: 1, title: 'Extraction Corridor', objective: 'Break the engine and disable MAGPIE', objectiveType: 'disable_target', targetState: TargetState.Exposed },
  ],
  spawns: [
    spawn('city_screen_1', 0.015, EnemyType.Sedan, 2, 2.8, 'escort'),
    spawn('city_bikes_1', 0.09, EnemyType.Motorcycle, 3, 3.4, 'flanker'),
    spawn('city_screen_2', 0.18, EnemyType.Sedan, 3, 3.2, 'interceptor'),
    spawn('city_drones_1', 0.26, EnemyType.Drone, 3, 3.8, 'aerial_harasser'),
    spawn('city_truck_1', 0.34, EnemyType.Truck, 1, 0, 'blocker'),
    spawn('city_bikes_2', 0.42, EnemyType.Motorcycle, 3, 3.5, 'flanker'),
    { ...spawn('city_target', TARGET_CONTACT_T, EnemyType.ArmoredTarget, 1, 0, 'target'), isTarget: true },
    spawn('city_screen_3', 0.52, EnemyType.Sedan, 3, 3.4, 'escort'),
    spawn('city_helicopter', 0.61, EnemyType.Helicopter, 1, 0, 'aerial_harasser'),
    spawn('city_trucks_2', 0.69, EnemyType.Truck, 2, 2.4, 'blocker'),
    spawn('city_bikes_3', 0.8, EnemyType.Motorcycle, 4, 3.6, 'flanker'),
    spawn('city_final_screen', 0.9, EnemyType.Sedan, 3, 3.2, 'escort'),
  ],
  obstacles: [
    obstacle('city_barriers_1', 0.07, ObstacleType.Barrier, [-3.5, 0]),
    obstacle('city_wreck_1', 0.14, ObstacleType.Wreck, [2.8]),
    obstacle('city_barrels_1', 0.23, ObstacleType.Barrel, [-3.8, -0.5, 3.4]),
    obstacle('city_repair_1', 0.28, ObstacleType.RepairKit, [7.4]),
    obstacle('city_wreck_2', 0.32, ObstacleType.Wreck, [-2.7, 2.8]),
    obstacle('city_barriers_2', 0.44, ObstacleType.Barrier, [3.4]),
    obstacle('city_wreck_3', 0.55, ObstacleType.Wreck, [-3.5, 0.8]),
    obstacle('city_repair_2', 0.63, ObstacleType.RepairKit, [-7.4]),
    obstacle('city_barrels_2', 0.67, ObstacleType.Barrel, [-2.8, 2.8]),
    obstacle('city_barriers_3', 0.78, ObstacleType.Barrier, [-3.2, 0.2]),
    obstacle('city_wreck_4', 0.91, ObstacleType.Wreck, [3.2]),
  ],
  hazards: [
    hazard('city_crossing_1', 0.2, HazardType.TrafficCrossing, -2.2, 'CROSS TRAFFIC', 16, 2.1),
    hazard('city_crossing_2', 0.38, HazardType.TrafficCrossing, 2.4, 'CROSS TRAFFIC', 16, 2.1),
    hazard('city_lane_1', 0.49, HazardType.LaneStrike, 0, 'INTERSECTION BLOCKADE', 18, 2.2),
    hazard('city_crossing_3', 0.73, HazardType.TrafficCrossing, -2.8, 'CROSS TRAFFIC', 18, 2.1),
  ],
};

const tunnel: StageDefinition = {
  id: 'tunnel',
  order: 3,
  theme: LevelTheme.Tunnel,
  seed: 'lucky-tunnel-03',
  title: 'Blackout Run',
  location: 'Harbour Service Tunnels',
  briefing: 'Stay on the target through blackouts, use firelight to read the road, and hit exposed components in the maintenance bay.',
  timeLimitSeconds: 185,
  target: target('wraith', 'WRAITH', 610, 8000),
  beats: [
    { id: 'tunnel_descent', startsAt: 0, endsAt: 0.22, title: 'Descent', objective: 'Clear the tunnel interceptors', objectiveType: 'pursue', targetState: TargetState.Unknown },
    { id: 'tunnel_blackout', startsAt: 0.22, endsAt: TARGET_CONTACT_T, title: 'Blackout', objective: 'Survive the dark and trace WRAITH', objectiveType: 'survive_hazard', targetState: TargetState.Unknown },
    { id: 'tunnel_contact', startsAt: TARGET_CONTACT_T, endsAt: 0.7, title: 'Emergency Lighting', objective: 'Acquire WRAITH through the fire line', objectiveType: 'make_contact', targetState: TargetState.Pursuit },
    { id: 'tunnel_bay', startsAt: 0.7, endsAt: 1, title: 'Maintenance Bay', objective: 'Break exposed components and disable WRAITH', objectiveType: 'break_component', targetState: TargetState.Exposed },
  ],
  spawns: [
    spawn('tunnel_bikes_1', 0.015, EnemyType.Motorcycle, 2, 2.6, 'flanker'),
    spawn('tunnel_screen_1', 0.09, EnemyType.Sedan, 2, 2.8, 'escort'),
    spawn('tunnel_drones_1', 0.18, EnemyType.Drone, 2, 3.2, 'aerial_harasser'),
    spawn('tunnel_bikes_2', 0.26, EnemyType.Motorcycle, 3, 3.4, 'flanker'),
    spawn('tunnel_truck_1', 0.35, EnemyType.Truck, 1, 0, 'blocker'),
    spawn('tunnel_screen_2', 0.43, EnemyType.Sedan, 2, 3, 'interceptor'),
    { ...spawn('tunnel_target', TARGET_CONTACT_T, EnemyType.ArmoredTarget, 1, 0, 'target'), isTarget: true },
    spawn('tunnel_drones_2', 0.52, EnemyType.Drone, 3, 3.6, 'aerial_harasser'),
    spawn('tunnel_bikes_3', 0.61, EnemyType.Motorcycle, 3, 3.3, 'flanker'),
    spawn('tunnel_trucks_2', 0.7, EnemyType.Truck, 2, 2.6, 'blocker'),
    spawn('tunnel_screen_3', 0.81, EnemyType.Sedan, 3, 3.2, 'escort'),
    spawn('tunnel_final_drones', 0.91, EnemyType.Drone, 3, 3.7, 'aerial_harasser'),
  ],
  obstacles: [
    obstacle('tunnel_barrels_1', 0.07, ObstacleType.Barrel, [-2.8, 2.8]),
    obstacle('tunnel_barrier_1', 0.16, ObstacleType.Barrier, [0, 3.8]),
    obstacle('tunnel_wreck_1', 0.25, ObstacleType.Wreck, [-3.4]),
    obstacle('tunnel_repair_1', 0.3, ObstacleType.RepairKit, [-6.9]),
    obstacle('tunnel_barrels_2', 0.34, ObstacleType.Barrel, [-3.8, -0.6, 3.3]),
    obstacle('tunnel_barrier_2', 0.46, ObstacleType.Barrier, [-2.6, 2.7]),
    obstacle('tunnel_wreck_2', 0.58, ObstacleType.Wreck, [3.4]),
    obstacle('tunnel_repair_2', 0.64, ObstacleType.RepairKit, [6.9]),
    obstacle('tunnel_barrels_3', 0.69, ObstacleType.Barrel, [-3.5, 0.5]),
    obstacle('tunnel_barrier_3', 0.82, ObstacleType.Barrier, [-2.8, 2.8]),
    obstacle('tunnel_wreck_3', 0.92, ObstacleType.Wreck, [-3.4]),
  ],
  hazards: [
    hazard('tunnel_fire_1', 0.2, HazardType.TunnelFire, -2.6, 'TUNNEL FIRE', 18, 2),
    hazard('tunnel_rockfall_1', 0.4, HazardType.Rockfall, 2.4, 'ROCKFALL', 20, 1.9),
    hazard('tunnel_fire_2', 0.57, HazardType.TunnelFire, 0.2, 'TUNNEL FIRE', 18, 2),
    hazard('tunnel_rockfall_2', 0.76, HazardType.Rockfall, -2.8, 'ROCKFALL', 20, 1.9),
  ],
};

const desert: StageDefinition = {
  id: 'desert',
  order: 4,
  theme: LevelTheme.Desert,
  seed: 'lucky-desert-04',
  title: 'Long Sight',
  location: 'Western Test Range',
  briefing: 'Use long sightlines between dust bursts, stop the interceptor from reaching the airstrip, and survive its heaviest escort.',
  timeLimitSeconds: 205,
  target: target('jackal', 'JACKAL', 680, 10000),
  beats: [
    { id: 'desert_range', startsAt: 0, endsAt: 0.22, title: 'Open Range', objective: 'Break the forward patrol', objectiveType: 'pursue', targetState: TargetState.Unknown },
    { id: 'desert_ambush', startsAt: 0.22, endsAt: TARGET_CONTACT_T, title: 'Dust Ambush', objective: 'Survive the flankers and locate JACKAL', objectiveType: 'survive_hazard', targetState: TargetState.Unknown },
    { id: 'desert_contact', startsAt: TARGET_CONTACT_T, endsAt: 0.72, title: 'Convoy Break', objective: 'Hold JACKAL in range and break the heavy escort', objectiveType: 'make_contact', targetState: TargetState.Pursuit },
    { id: 'desert_airstrip', startsAt: 0.72, endsAt: 1, title: 'Airstrip', objective: 'Disable JACKAL before the runway', objectiveType: 'disable_target', targetState: TargetState.Exposed },
  ],
  spawns: [
    spawn('desert_screen_1', 0.015, EnemyType.Sedan, 2, 3, 'escort'),
    spawn('desert_bikes_1', 0.085, EnemyType.Motorcycle, 3, 3.7, 'flanker'),
    spawn('desert_drones_1', 0.17, EnemyType.Drone, 3, 4, 'aerial_harasser'),
    spawn('desert_truck_1', 0.25, EnemyType.Truck, 1, 0, 'blocker'),
    spawn('desert_helicopter_1', 0.34, EnemyType.Helicopter, 1, 0, 'aerial_harasser'),
    spawn('desert_screen_2', 0.42, EnemyType.Sedan, 3, 3.5, 'interceptor'),
    { ...spawn('desert_target', TARGET_CONTACT_T, EnemyType.ArmoredTarget, 1, 0, 'target'), isTarget: true },
    spawn('desert_trucks_2', 0.51, EnemyType.Truck, 2, 2.8, 'blocker'),
    spawn('desert_bikes_2', 0.6, EnemyType.Motorcycle, 4, 3.8, 'flanker'),
    spawn('desert_helicopter_2', 0.69, EnemyType.Helicopter, 1, 0, 'aerial_harasser'),
    spawn('desert_drones_2', 0.79, EnemyType.Drone, 4, 4, 'aerial_harasser'),
    spawn('desert_final_screen', 0.9, EnemyType.Sedan, 3, 3.4, 'escort'),
  ],
  obstacles: [
    obstacle('desert_rocks_1', 0.07, ObstacleType.Rock, [-3.5, 2.6]),
    obstacle('desert_wreck_1', 0.16, ObstacleType.Wreck, [3.4]),
    obstacle('desert_rocks_2', 0.25, ObstacleType.Rock, [-2.8, 2.8]),
    obstacle('desert_repair_1', 0.3, ObstacleType.RepairKit, [7.8]),
    obstacle('desert_barrels_1', 0.35, ObstacleType.Barrel, [-3.8, -0.4, 3.4]),
    obstacle('desert_wreck_2', 0.47, ObstacleType.Wreck, [-3.4, 0.5]),
    obstacle('desert_rocks_3', 0.59, ObstacleType.Rock, [2.8]),
    obstacle('desert_repair_2', 0.65, ObstacleType.RepairKit, [-7.8]),
    obstacle('desert_barrels_2', 0.7, ObstacleType.Barrel, [-2.8, 2.7]),
    obstacle('desert_wreck_3', 0.82, ObstacleType.Wreck, [-3.2]),
    obstacle('desert_rocks_4', 0.93, ObstacleType.Rock, [-2.5, 2.9]),
  ],
  hazards: [
    hazard('desert_dust_1', 0.2, HazardType.DustBurst, -2.4, 'DUST BURST', 14, 2.2),
    hazard('desert_dust_2', 0.39, HazardType.DustBurst, 2.7, 'DUST BURST', 14, 2.2),
    hazard('desert_rockfall_1', 0.56, HazardType.Rockfall, 0.1, 'MESA COLLAPSE', 22, 2),
    hazard('desert_lane_1', 0.77, HazardType.LaneStrike, -2.6, 'ESCORT STRIKE', 20, 2),
  ],
};

const swamp: StageDefinition = {
  id: 'swamp',
  order: 5,
  theme: LevelTheme.Swamp,
  seed: 'lucky-swamp-05',
  title: 'Deadwater',
  location: 'Deadwater Causeway',
  briefing: 'Final operation. Read every hazard language, dismantle the full escort, and capture the network controller alive.',
  timeLimitSeconds: 190,
  target: target('kingfisher', 'KINGFISHER', 760, 15000),
  beats: [
    { id: 'swamp_causeway', startsAt: 0, endsAt: 0.22, title: 'Causeway', objective: 'Dismantle the forward screen', objectiveType: 'pursue', targetState: TargetState.Unknown },
    { id: 'swamp_gauntlet', startsAt: 0.22, endsAt: TARGET_CONTACT_T, title: 'Deadwater Gauntlet', objective: 'Survive the eruptions and trace KINGFISHER', objectiveType: 'survive_hazard', targetState: TargetState.Unknown },
    { id: 'swamp_contact', startsAt: TARGET_CONTACT_T, endsAt: 0.68, title: 'Flooded Cut', objective: 'Acquire KINGFISHER beyond the wrecks', objectiveType: 'make_contact', targetState: TargetState.Pursuit },
    { id: 'swamp_mastery', startsAt: 0.68, endsAt: 1, title: 'Final Capture', objective: 'Break components and capture KINGFISHER alive', objectiveType: 'capture_target', targetState: TargetState.Exposed },
  ],
  spawns: [
    spawn('swamp_bikes_1', 0.012, EnemyType.Motorcycle, 3, 3.5, 'flanker'),
    spawn('swamp_drones_1', 0.08, EnemyType.Drone, 3, 3.8, 'aerial_harasser'),
    spawn('swamp_screen_1', 0.15, EnemyType.Sedan, 3, 3.3, 'escort'),
    spawn('swamp_truck_1', 0.23, EnemyType.Truck, 1, 0, 'blocker'),
    spawn('swamp_helicopter_1', 0.31, EnemyType.Helicopter, 1, 0, 'aerial_harasser'),
    spawn('swamp_bikes_2', 0.39, EnemyType.Motorcycle, 4, 3.7, 'flanker'),
    { ...spawn('swamp_target', TARGET_CONTACT_T, EnemyType.ArmoredTarget, 1, 0, 'target'), isTarget: true },
    spawn('swamp_screen_2', 0.48, EnemyType.Sedan, 3, 3.5, 'interceptor'),
    spawn('swamp_trucks_2', 0.57, EnemyType.Truck, 2, 2.8, 'blocker'),
    spawn('swamp_drones_2', 0.66, EnemyType.Drone, 4, 4, 'aerial_harasser'),
    spawn('swamp_helicopter_2', 0.76, EnemyType.Helicopter, 1, 0, 'aerial_harasser'),
    spawn('swamp_final_bikes', 0.85, EnemyType.Motorcycle, 4, 3.8, 'flanker'),
    spawn('swamp_final_screen', 0.92, EnemyType.Sedan, 3, 3.4, 'escort'),
  ],
  obstacles: [
    obstacle('swamp_wreck_1', 0.06, ObstacleType.Wreck, [-3.5, 1.8]),
    obstacle('swamp_barrels_1', 0.14, ObstacleType.Barrel, [-3.8, -0.6, 3.4]),
    obstacle('swamp_rocks_1', 0.23, ObstacleType.Rock, [-2.7, 2.8]),
    obstacle('swamp_repair_1', 0.29, ObstacleType.RepairKit, [-7.2]),
    obstacle('swamp_barrier_1', 0.33, ObstacleType.Barrier, [3.5]),
    obstacle('swamp_wreck_2', 0.44, ObstacleType.Wreck, [-3.4, 0.4]),
    obstacle('swamp_rocks_2', 0.55, ObstacleType.Rock, [2.8]),
    obstacle('swamp_repair_2', 0.63, ObstacleType.RepairKit, [7.2]),
    obstacle('swamp_barrels_2', 0.67, ObstacleType.Barrel, [-3.6, 0, 3.5]),
    obstacle('swamp_wreck_3', 0.79, ObstacleType.Wreck, [-2.8, 2.7]),
    obstacle('swamp_barrier_2', 0.9, ObstacleType.Barrier, [-3.3]),
  ],
  hazards: [
    hazard('swamp_eruption_1', 0.18, HazardType.SwampEruption, -2.6, 'METHANE ERUPTION', 18, 2),
    hazard('swamp_cargo_1', 0.36, HazardType.CargoDrop, 2.8, 'FALLING CARGO', 18, 1.9),
    hazard('swamp_crossing_1', 0.52, HazardType.TrafficCrossing, -2.4, 'CAUSEWAY CROSSING', 20, 2.1),
    hazard('swamp_fire_1', 0.7, HazardType.TunnelFire, 0.2, 'BURNING WRECK', 20, 2),
    hazard('swamp_eruption_2', 0.84, HazardType.SwampEruption, 2.6, 'METHANE ERUPTION', 22, 2),
  ],
};

export const STAGE_CATALOG: readonly StageDefinition[] = validateStageCatalog([
  countryside,
  city,
  tunnel,
  desert,
  swamp,
]).slice().sort((a, b) => a.order - b.order);

export const CAMPAIGN_STAGE_COUNT = STAGE_CATALOG.length;

export function getStageDefinition(stageId: StageId): StageDefinition {
  const stage = STAGE_CATALOG.find((candidate) => candidate.id === stageId);
  if (!stage) throw new Error(`Unknown stage "${stageId}"`);
  return stage;
}

export function getStageForLevel(level: number): StageDefinition {
  const index = Math.max(0, Math.min(STAGE_CATALOG.length - 1, Math.floor(level) - 1));
  return STAGE_CATALOG[index];
}

export function getBeatAt(stage: StageDefinition, progress: number) {
  const clamped = Math.max(0, Math.min(0.999_999, progress));
  return stage.beats.find((beat) => clamped >= beat.startsAt && clamped < beat.endsAt) ?? stage.beats.at(-1)!;
}
